/**
 * features/admissions/PendingList.tsx
 * ------------------------------------------------------------------
 * "Pending" tab in the Admissions module. Lists students in the
 * holding state — `status:'pending'` — people who exist in Pastel
 * (finance) but are NOT yet enrolled. They carry whatever we already
 * have (name, Pastel code, seeded opening balance) and a '' gender
 * sentinel until their real info arrives and they're enrolled.
 *
 * EDITABLE: open a pending pupil's skeleton profile (name -> full
 * profile), fill in the details we have (gender + intended class),
 * and Enrol them into their class when ready. Their finance balance is
 * untouched by enrolment. Gated canManageStudents.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { Clock, Search, Pencil, Check, X, GraduationCap, ExternalLink } from 'lucide-react';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { db, updateDoc } from '@/lib/firebase';
import { doc } from 'firebase/firestore';
import { getPermissions } from '@/lib/permissions';
import { auditFromContext } from '@/lib/audit';
import { SoftCard } from '@/features/finance/ui';

export default function PendingList() {
  const { students, classes, classById } = useData();
  const { userData } = useAuth();
  const canManage = useMemo(
    () => getPermissions(userData?.roles ?? userData?.role ?? null).canManageStudents,
    [userData],
  );
  const [q, setQ] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editGender, setEditGender] = useState<'' | 'M' | 'F'>('');
  const [editClass, setEditClass] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const classOptions = useMemo(
    () => classes.filter((c) => !c.archived),
    [classes],
  );

  const pending = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return students
      .filter((s) => s.status === 'pending')
      .filter(
        (s) =>
          !needle ||
          s.fullName.toLowerCase().includes(needle) ||
          (s.pastelCode ?? '').toLowerCase().includes(needle) ||
          (s.admissionNumber ?? '').toLowerCase().includes(needle),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [students, q]);

  const genderLabel = (g: string) => (g === 'M' ? 'Male' : g === 'F' ? 'Female' : '—');
  const openProfile = (id: string) => { window.location.hash = `#/studentProfile/${id}`; };

  function startEdit(s: (typeof pending)[number]) {
    setEditId(s.id);
    setEditGender((s.gender as '' | 'M' | 'F') || '');
    setEditClass(s.currentClass || '');
    setMsg(null);
  }

  async function saveEdit(id: string) {
    setBusy(true);
    try {
      await updateDoc(doc(db, COLLECTIONS.students, id), {
        gender: editGender, currentClass: editClass,
        updatedAt: new Date().toISOString(), updatedBy: userData?.uid ?? '',
      });
      setEditId(null);
      setMsg({ ok: true, text: 'Details saved.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  async function enrol(id: string) {
    if (editGender !== 'M' && editGender !== 'F') { setMsg({ ok: false, text: 'Set a gender before enrolling.' }); return; }
    if (!editClass) { setMsg({ ok: false, text: 'Choose an intended class before enrolling.' }); return; }
    setBusy(true);
    try {
      const now = new Date().toISOString();
      await updateDoc(doc(db, COLLECTIONS.students, id), {
        gender: editGender, currentClass: editClass, status: 'active',
        exitReason: null, exitDate: null, updatedAt: now, updatedBy: userData?.uid ?? '',
      });
      const s = students.find((x) => x.id === id);
      auditFromContext(userData, 'student_enrolled', 'admissions', {
        targetId: id, targetType: 'student', targetName: s?.fullName,
        details: { from: 'pending', class: classById(editClass)?.name },
      });
      setEditId(null);
      setMsg({ ok: true, text: `Enrolled into ${classById(editClass)?.name ?? 'class'}.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <SoftCard>
        <div className="flex items-start gap-3">
          <Clock size={20} className="text-primaryLight shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold">Pending students</h3>
            <p className="text-sm text-gray-500">
              In Pastel (finance) but not yet enrolled. Their balances show in Finance;
              open a pupil to fill in the missing details and enrol them into a class when
              ready. Gender and a real class are required before enrolment.
            </p>
          </div>
        </div>
      </SoftCard>

      {msg && (
        <div className={`text-sm rounded-lg px-4 py-2 ${msg.ok ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
          {msg.text}
        </div>
      )}

      <SoftCard>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / Pastel code…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:outline-none focus:border-primaryLight"
            />
          </div>
          <span className="text-xs text-gray-400 shrink-0">{pending.length} pending</span>
        </div>

        {pending.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">
            No pending students.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 px-3 font-medium">Pastel code</th>
                  <th className="py-2 px-3 font-medium">Adm. no.</th>
                  <th className="py-2 px-3 font-medium">Gender</th>
                  <th className="py-2 px-3 font-medium">Intended class</th>
                  {canManage && <th className="py-2 pl-3 font-medium text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {pending.map((s) => {
                  const editing = editId === s.id;
                  return (
                    <tr key={s.id} className={editing ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}>
                      <td className="py-2 pr-3 font-medium">
                        <button onClick={() => openProfile(s.id)} className="inline-flex items-center gap-1 text-primaryLight dark:text-blue-300 hover:underline">
                          {s.fullName} <ExternalLink size={12} className="opacity-60" />
                        </button>
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-600 dark:text-gray-300">{s.pastelCode ?? '—'}</td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{s.admissionNumber ?? '—'}</td>
                      <td className="py-2 px-3">
                        {editing ? (
                          <select value={editGender} onChange={(e) => setEditGender(e.target.value as '' | 'M' | 'F')} className="rounded border border-gray-200 dark:border-gray-600 dark:bg-gray-800 px-2 py-1 text-sm">
                            <option value="">—</option><option value="M">Male</option><option value="F">Female</option>
                          </select>
                        ) : s.gender === '' ? (
                          <span className="text-xs text-amber-600 dark:text-amber-400">needs gender</span>
                        ) : genderLabel(s.gender)}
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-300">
                        {editing ? (
                          <select value={editClass} onChange={(e) => setEditClass(e.target.value)} className="rounded border border-gray-200 dark:border-gray-600 dark:bg-gray-800 px-2 py-1 text-sm max-w-[10rem]">
                            <option value="">— choose —</option>
                            {classOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        ) : (classById(s.currentClass)?.name ?? '—')}
                      </td>
                      {canManage && (
                        <td className="py-2 pl-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {editing ? (
                              <>
                                <button onClick={() => enrol(s.id)} disabled={busy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40">
                                  <GraduationCap size={13} /> Enrol
                                </button>
                                <button onClick={() => saveEdit(s.id)} disabled={busy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40">
                                  <Check size={13} /> Save
                                </button>
                                <button onClick={() => setEditId(null)} disabled={busy} className="p-1 rounded-lg text-gray-400 hover:text-gray-600">
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => startEdit(s)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                <Pencil size={12} /> Edit
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>
    </div>
  );
}
